import { useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUser } from '@/store/userSlice';
import { useLoginMicrosoftMutation } from '@/services/authApi';
import { Box, CircularProgress, Typography, Container, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';

const scopes = ['openid', 'profile', 'email'];

export default function SsoMicrosoft() {
  const { instance } = useMsal();
  const [loginMicrosoft] = useLoginMicrosoftMutation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const run = async () => {
      try {
        // Handle the redirect result (if present)
        const result = await instance.handleRedirectPromise();

        // Determine account
        const account =
          result?.account || instance.getActiveAccount() || instance.getAllAccounts()[0];
        if (account) {
          instance.setActiveAccount(account);
        }

        // Get ID token
        let idToken = result?.idToken;
        if (!idToken) {
          if (!account) throw new Error('No Microsoft account found after redirect');
          const silentRequest: SilentRequest = { account, scopes };
          try {
            const silentResult = await instance.acquireTokenSilent(silentRequest);
            idToken = silentResult.idToken;
          } catch (e) {
            if (e instanceof InteractionRequiredAuthError) {
              // Should be rare immediately after redirect, but fallback
              throw new Error('Unable to acquire ID token silently');
            }
            throw e;
          }
        }

        // Exchange with backend
        const resp = await loginMicrosoft({ idToken }).unwrap();

        // Update user store (mirror Login.tsx success path)
        const userData = {
          userId: resp.userId || null,
          name: resp.name || null,
          email: resp.email || null,
          role: resp.role || null,
          jwtToken: resp.jwtToken || null,
          amcIds: resp.amcIds,
          amcAccess: resp.amcAccess,
        };
        dispatch(setUser(userData));

        // Navigate to intended path
        const from = sessionStorage.getItem('sso_from') || '/';
        sessionStorage.removeItem('sso_from');
        navigate(from, { replace: true });
      } catch (err: any) {
        enqueueSnackbar(err?.data?.message || 'Failed to login', { variant: 'error' });
        // On error, go back to login
        navigate('/login', { replace: true });
      }
    };

    run();
  }, [dispatch, instance, loginMicrosoft, navigate]);

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%', textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Completing Microsoft sign-in…
          </Typography>
          <CircularProgress />
        </Paper>
      </Box>
    </Container>
  );
}
